import React, { useState, useEffect } from 'react';
import { lecturersAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useNavigate } from 'react-router-dom';

const LecturersList = () => {
  const [lecturers, setLecturers] = useState([]);
  const [allLecturers, setAllLecturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    employeeId: '',
    lecturerName: '',
    gender: 'all',
    department: '',
    module: '',
    status: 'all'
  });

  const navigate = useNavigate();

  const fetchLecturers = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('=== FRONTEND LECTURERS FETCH DEBUG ===');
      console.log('Attempting to fetch lecturers...');
      
      const response = await lecturersAPI.getAllLecturers();
      console.log('API Response received:', response);
      console.log('Response data:', response.data);
      console.log('Data type:', typeof response.data);
      console.log('Data length:', Array.isArray(response.data) ? response.data.length : 'Not an array');
      
      const lecturersData = response.data || [];
      console.log('Processed lecturers data:', lecturersData);
      
      if (lecturersData.length === 0) {
        console.warn('No lecturers data received from API');
      } else {
        console.log('Sample lecturer record:', lecturersData[0]);
      }
      
      setAllLecturers(lecturersData);
      setLecturers(lecturersData);
    } catch (error) {
      console.error('Error fetching lecturers:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setError(error.response?.data?.error || error.message || 'Failed to fetch lecturers');
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering function
  const applyFilters = () => {
    let filteredLecturers = [...allLecturers];

    // Apply employee ID filter
    if (filters.employeeId && filters.employeeId.trim()) {
      filteredLecturers = filteredLecturers.filter(lecturer => 
        lecturer.employee_id?.toLowerCase().includes(filters.employeeId.toLowerCase())
      );
    }

    // Apply lecturer name filter
    if (filters.lecturerName && filters.lecturerName.trim()) {
      filteredLecturers = filteredLecturers.filter(lecturer => 
        lecturer.name?.toLowerCase().includes(filters.lecturerName.toLowerCase())
      );
    }

    // Apply gender filter
    if (filters.gender && filters.gender !== 'all') {
      filteredLecturers = filteredLecturers.filter(lecturer => 
        lecturer.gender === filters.gender
      );
    }

    // Apply department filter
    if (filters.department && filters.department.trim()) {
      filteredLecturers = filteredLecturers.filter(lecturer => 
        lecturer.department?.toLowerCase().includes(filters.department.toLowerCase())
      );
    }

    // Apply module filter
    if (filters.module && filters.module.trim()) {
      filteredLecturers = filteredLecturers.filter(lecturer => {
        if (!lecturer.staff_courses || lecturer.staff_courses.length === 0) return false;
        return lecturer.staff_courses.some(course => 
          course.course_name?.toLowerCase().includes(filters.module.toLowerCase()) ||
          course.modules?.module_name?.toLowerCase().includes(filters.module.toLowerCase()) ||
          course.modules?.module_code?.toLowerCase().includes(filters.module.toLowerCase())
        );
      });
    }

    // Apply status filter
    if (filters.status && filters.status !== 'all') {
      const isActive = filters.status === 'Active';
      filteredLecturers = filteredLecturers.filter(lecturer => 
        lecturer.active === isActive
      );
    }

    setLecturers(filteredLecturers);
  };

  useEffect(() => {
    fetchLecturers();
  }, []);

  const filterOptions = {
    status: ['all', 'Active', 'Inactive'],
    gender: ['all', 'Male', 'Female', 'Other']
  };

  const handleSearch = () => {
    applyFilters();
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Reset all filters to default values
  const handleReset = () => {
    setFilters({
      employeeId: '',
      lecturerName: '',
      gender: 'all',
      department: '',
      module: '',
      status: 'all'
    });
    setLecturers(allLecturers); // Show all lecturers
  };

  // Download CSV function
  const downloadCSV = () => {
    console.log('=== Downloading Lecturers CSV ===');
    console.log('Lecturers to export:', lecturers.length);
    
    if (!lecturers || lecturers.length === 0) {
      alert('No lecturers data to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'No.',
      'Employee ID',
      'Name',
      'Email',
      'Department',
      'Staff Type',
      'Gender',
      'Contact No',
      'Address',
      'Modules/Courses',
      'Total Modules',
      'Status'
    ];

    // Convert lecturers data to CSV rows
    const csvData = lecturers.map((lecturer, index) => {
      // Prepare modules information
      const modulesList = lecturer.staff_courses && lecturer.staff_courses.length > 0 
        ? lecturer.staff_courses.map(course => course.course_name || 'N/A').join('; ')
        : 'No modules assigned';
      
      const modulesCount = lecturer.staff_courses ? lecturer.staff_courses.length : 0;

      return [
        (index + 1).toString().padStart(2, '0'),
        lecturer.employee_id || 'N/A',
        lecturer.name || 'N/A',
        lecturer.email || 'N/A',
        lecturer.department || 'N/A',
        lecturer.staff_type || 'N/A',
        lecturer.gender || 'N/A',
        lecturer.contact_no || 'N/A',
        lecturer.address || 'N/A',
        modulesList,
        modulesCount,
        lecturer.active ? 'Active' : 'Inactive'
      ];
    });

    // Combine headers and data
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => 
        // Escape fields containing commas, quotes, or newlines
        typeof field === 'string' && (field.includes(',') || field.includes('"') || field.includes('\n'))
          ? `"${field.replace(/"/g, '""')}"` 
          : field
      ).join(','))
      .join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    // Generate descriptive filename with timestamp and filter info
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const activeFilters = [];
    
    if (filters.employeeId) activeFilters.push(`empId-${filters.employeeId}`);
    if (filters.lecturerName) activeFilters.push(`name-${filters.lecturerName.replace(/\s+/g, '-')}`);
    if (filters.gender !== 'all') activeFilters.push(`gender-${filters.gender}`);
    if (filters.department) activeFilters.push(`dept-${filters.department.replace(/\s+/g, '-')}`);
    if (filters.module) activeFilters.push(`module-${filters.module.replace(/\s+/g, '-')}`);
    if (filters.status !== 'all') activeFilters.push(`status-${filters.status}`);
    
    const filterInfo = activeFilters.length > 0 
      ? `_filtered-by-${activeFilters.join('-')}` 
      : '_all-lecturers';
    const filename = `lecturers-list${filterInfo}_${timestamp}.csv`;
    
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log(`✅ CSV downloaded: ${filename}`);
    console.log(`📊 Exported ${lecturers.length} lecturers`);
    
    // Show success message
    const filterMessage = Object.values(filters).some(value => value !== '' && value !== 'all') 
      ? ` with applied filters` 
      : '';
    alert(`Successfully exported ${lecturers.length} lecturers${filterMessage} to CSV file: ${filename}`);
  };

  const FilterDropdown = ({ label, name, options, value }) => (
    <div style={{position: 'relative', width: 164}}>
      <div style={{opacity: 0.65, color: '#444444', fontSize: 16, fontFamily: 'Outfit', marginBottom: 8}}>
        {label}
      </div>
      <select
        value={value}
        onChange={(e) => handleFilterChange(name, e.target.value)}
        style={{
          width: '100%',
          height: 58,
          background: 'white',
          border: '1px solid #CED4DA',
          borderRadius: 6,
          padding: '0 15px',
          fontSize: 16,
          fontFamily: 'Outfit'
        }}
      >
        {options.map(option => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div style={{display: 'flex', minHeight: '100vh', background: 'white'}}>
      <Sidebar />
      <div style={{flex: 1, marginLeft: 290, padding: '0 40px 40px', overflow: 'auto'}}>
        <Header />
        
        <div style={{marginTop: 40, background: '#FFFFFF', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.05)'}}>
          <div style={{
            display: 'flex', 
            gap: 20, 
            marginTop: 40, 
            alignItems: 'flex-end',
            flexWrap: 'wrap',
            padding: '20px'
          }}>
            <div style={{width: 164}}>
              <div style={{opacity: 0.65, color: '#444444', fontSize: 16, fontFamily: 'Outfit', marginBottom: 8}}>
                Employee ID
              </div>
              <input
                type="text"
                value={filters.employeeId}
                onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                placeholder="Enter employee ID"
                style={{
                  width: '100%',
                  height: 58,
                  background: 'white',
                  border: '1px solid #CED4DA',
                  borderRadius: 6,
                  padding: '0 15px',
                  fontSize: 16,
                  fontFamily: 'Outfit'
                }}
              />
            </div>
            <div style={{width: 164}}>
              <div style={{opacity: 0.65, color: '#444444', fontSize: 16, fontFamily: 'Outfit', marginBottom: 8}}>
                Lecturer Name
              </div>
              <input
                type="text"
                value={filters.lecturerName}
                onChange={(e) => handleFilterChange('lecturerName', e.target.value)}
                placeholder="Enter name"
                style={{
                  width: '100%',
                  height: 58,
                  background: 'white',
                  border: '1px solid #CED4DA',
                  borderRadius: 6,
                  padding: '0 15px',
                  fontSize: 16,
                  fontFamily: 'Outfit'
                }}
              />
            </div>

            <FilterDropdown 
              label="Gender" 
              name="gender" 
              options={filterOptions.gender}
              value={filters.gender}
            />

            <div style={{width: 164}}>
              <div style={{opacity: 0.65, color: '#444444', fontSize: 16, fontFamily: 'Outfit', marginBottom: 8}}>
                Department
              </div>
              <input
                type="text"
                value={filters.department}
                onChange={(e) => handleFilterChange('department', e.target.value)}
                placeholder="Enter department"
                style={{
                  width: '100%',
                  height: 58,
                  background: 'white',
                  border: '1px solid #CED4DA',
                  borderRadius: 6,
                  padding: '0 15px',
                  fontSize: 16,
                  fontFamily: 'Outfit'
                }}
              />
            </div>

            <div style={{width: 164}}>
              <div style={{opacity: 0.65, color: '#444444', fontSize: 16, fontFamily: 'Outfit', marginBottom: 8}}>
                Module/Course
              </div>
              <input
                type="text"
                value={filters.module}
                onChange={(e) => handleFilterChange('module', e.target.value)}
                placeholder="Enter module name/code"
                style={{
                  width: '100%',
                  height: 58,
                  background: 'white',
                  border: '1px solid #CED4DA',
                  borderRadius: 6,
                  padding: '0 15px',
                  fontSize: 16,
                  fontFamily: 'Outfit'
                }}
              />
            </div>
            <FilterDropdown 
              label="Status" 
              name="status" 
              options={filterOptions.status}
              value={filters.status}
            />
            <button onClick={handleSearch} style={{
              width: 120,
              height: 52,
              background: '#F04915',
              borderRadius: 12,
              border: 'none',
              color: 'white',
              fontSize: 20,
              fontFamily: 'Poppins',
              cursor: 'pointer'
            }}>
              Search
            </button>
            
            <button onClick={handleReset} style={{
              width: 120,
              height: 52,
              background: '#6B7280',
              borderRadius: 12,
              border: 'none',
              color: 'white',
              fontSize: 20,
              fontFamily: 'Poppins',
              cursor: 'pointer'
            }}>
              Reset
            </button>
            
            <button 
              onClick={downloadCSV}
              disabled={!lecturers || lecturers.length === 0}
              style={{
                width: 140,
                height: 52,
                background: lecturers && lecturers.length > 0 ? '#10B981' : '#D1D5DB',
                borderRadius: 12,
                border: 'none',
                color: 'white',
                fontSize: 16,
                fontFamily: 'Poppins',
                fontWeight: '500',
                cursor: lecturers && lecturers.length > 0 ? 'pointer' : 'not-allowed',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
              onMouseOver={(e) => {
                if (lecturers && lecturers.length > 0) {
                  e.target.style.background = '#059669';
                }
              }}
              onMouseOut={(e) => {
                if (lecturers && lecturers.length > 0) {
                  e.target.style.background = '#10B981';
                }
              }}
              title={`Download ${lecturers?.length || 0} lecturers as CSV`}
            >
              📥 CSV Export
            </button>
          </div>
        </div>

        <div style={{marginTop: 30}}>
          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: '#F9FAFB',
              borderRadius: 8,
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: 18, fontFamily: 'Outfit', color: '#6B7280' }}>
                Loading lecturers...
              </div>
            </div>
          ) : error ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              background: '#FEF2F2',
              borderRadius: 8,
              border: '1px solid #FECACA'
            }}>
              <div style={{ fontSize: 18, fontFamily: 'Outfit', color: '#DC2626' }}>
                {error}
              </div>
              <button 
                onClick={fetchLecturers}
                style={{
                  marginTop: 15,
                  padding: '12px 24px',
                  backgroundColor: '#DC2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                  fontFamily: 'Outfit',
                  fontWeight: '500'
                }}
              >
                Retry
              </button>
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              {lecturers.length === 0 ? (
                <div style={{
                  textAlign: 'center', 
                  padding: '60px 20px',
                  background: '#F9FAFB'
                }}>
                  <div style={{
                    fontSize: 48,
                    marginBottom: 16
                  }}>👨‍🏫</div>
                  <div style={{
                    fontSize: 20,
                    fontWeight: '600',
                    marginBottom: 12,
                    color: '#374151',
                    fontFamily: 'Outfit'
                  }}>
                    No Lecturers Found
                  </div>
                  <div style={{
                    color: '#6B7280',
                    marginBottom: 24,
                    lineHeight: 1.6,
                    fontFamily: 'Outfit'
                  }}>
                    {allLecturers.length === 0 
                      ? "Your university currently has no lecturers in the system."
                      : "No lecturers match your current filter criteria."
                    }
                  </div>
                  
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                    {allLecturers.length === 0 && (
                      <button 
                        onClick={() => navigate('/add-staff')}
                        style={{
                          padding: '12px 24px',
                          backgroundColor: '#F04915',
                          color: 'white',
                          border: 'none',
                          borderRadius: 8,
                          cursor: 'pointer',
                          fontSize: 14,
                          fontFamily: 'Outfit',
                          fontWeight: '500'
                        }}
                      >
                        Add Lecturers
                      </button>
                    )}
                    
                    <button 
                      onClick={() => {
                        setFilters({
                          employeeId: '',
                          lecturerName: '',
                          gender: 'all',
                          department: '',
                          module: '',
                          status: 'all'
                        });
                        setLecturers(allLecturers);
                      }}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#6B7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 14,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}
                    >
                      Clear Filters
                    </button>
                    
                    <button 
                      onClick={fetchLecturers}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#2563EB',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 14,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}
                    >
                      Refresh Data
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 24,
                    flexWrap: 'wrap',
                    gap: 12
                  }}>
                    <div style={{
                      color: '#374151',
                      fontSize: 16,
                      fontFamily: 'Outfit',
                      fontWeight: '500'
                    }}>
                      Found {lecturers.length} lecturer{lecturers.length !== 1 ? 's' : ''} 
                      {Object.entries(filters).filter(([key, value]) => value !== '' && value !== 'all').length > 0 && (
                        <span style={{color: '#6B7280', marginLeft: 8}}>
                          (filtered from {allLecturers.length} total)
                        </span>
                      )}
                    </div>
                    
                    {lecturers.length > 0 && (
                      <button
                        onClick={downloadCSV}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#059669',
                          color: 'white',
                          border: 'none',
                          borderRadius: 8,
                          cursor: 'pointer',
                          fontSize: 14,
                          fontFamily: 'Outfit',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8
                        }}
                      >
                        📊 Download CSV
                      </button>
                    )}
                  </div>

                  <div style={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: 12,
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ backgroundColor: '#F9FAFB' }}>
                        <tr>
                          <th style={{
                            padding: '16px 20px',
                            textAlign: 'left',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '1px solid #E5E7EB'
                          }}>Employee ID</th>
                          <th style={{
                            padding: '16px 20px',
                            textAlign: 'left',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '1px solid #E5E7EB'
                          }}>Name</th>
                          <th style={{
                            padding: '16px 20px',
                            textAlign: 'left',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '1px solid #E5E7EB'
                          }}>Department</th>
                          <th style={{
                            padding: '16px 20px',
                            textAlign: 'left',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '1px solid #E5E7EB'
                          }}>Staff Type</th>
                          <th style={{
                            padding: '16px 20px',
                            textAlign: 'left',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '1px solid #E5E7EB'
                          }}>Gender</th>
                          <th style={{
                            padding: '16px 20px',
                            textAlign: 'left',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '1px solid #E5E7EB'
                          }}>Modules</th>
                          <th style={{
                            padding: '16px 20px',
                            textAlign: 'left',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '1px solid #E5E7EB'
                          }}>Status</th>
                          <th style={{
                            padding: '16px 20px',
                            textAlign: 'center',
                            fontSize: 14,
                            fontFamily: 'Outfit',
                            fontWeight: '600',
                            color: '#374151',
                            borderBottom: '1px solid #E5E7EB'
                          }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lecturers.map((lecturer, index) => (
                          <tr key={lecturer.id || index} style={{
                            borderBottom: index < lecturers.length - 1 ? '1px solid #F3F4F6' : 'none'
                          }}>
                            <td style={{
                              padding: '16px 20px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151'
                            }}>
                              {lecturer.employee_id || '-'}
                            </td>
                            <td style={{
                              padding: '16px 20px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#374151',
                              fontWeight: '500'
                            }}>
                              {lecturer.name || '-'}
                            </td>
                            <td style={{
                              padding: '16px 20px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#6B7280'
                            }}>
                              {lecturer.department || '-'}
                            </td>
                            <td style={{
                              padding: '16px 20px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#6B7280'
                            }}>
                              {lecturer.staff_type || '-'}
                            </td>
                            <td style={{
                              padding: '16px 20px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#6B7280'
                            }}>
                              {lecturer.gender || '-'}
                            </td>
                            <td style={{
                              padding: '16px 20px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              color: '#6B7280'
                            }}>
                              {lecturer.staff_courses && lecturer.staff_courses.length > 0 
                                ? lecturer.staff_courses.map(course => course.course_name).join(', ')
                                : '-'
                              }
                            </td>
                            <td style={{
                              padding: '16px 20px',
                              fontSize: 14,
                              fontFamily: 'Outfit',
                              fontWeight: '500'
                            }}>
                              <span style={{
                                padding: '4px 12px',
                                borderRadius: 20,
                                fontSize: 12,
                                fontWeight: '600',
                                backgroundColor: lecturer.active ? '#D1FAE5' : '#FEE2E2',
                                color: lecturer.active ? '#059669' : '#DC2626'
                              }}>
                                {lecturer.active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td style={{
                              padding: '16px 20px',
                              textAlign: 'center'
                            }}>
                              <button 
                                onClick={() => navigate(`/edit-staff/${lecturer.id}`)}
                                style={{
                                  backgroundColor: '#F59E0B',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 6,
                                  padding: '8px 16px',
                                  fontSize: 12,
                                  fontFamily: 'Outfit',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4
                                }}
                              >
                                ✏️ Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LecturersList;
